import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { DialogConnectionErrorComponent } from '@common/components/dialog-connection-error.component';
import { DialogErrorComponent } from '@common/components/dialog-error.component';
import { DialogService } from '@common/services/dialog.service';
import { WebsocketService } from '@common/services/websocket.service';
import _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class DialogErrorHandler implements ErrorHandler {
    private connectionErrorDialogOpen = false;

    constructor(
        private dialogService: DialogService,
        private websocketService: WebsocketService
    ) {}

    handleError(error): void {
        if (!error || error.rejection?.error === 'Token-Expired') return;
        if (error.rejection?.httpResponse) error = error.rejection.httpResponse.response; // breeze query error

        if (error.rejection?.error?.validationErrors) {
            this.handleServerSideError(error.rejection);
        } else if (error instanceof HttpErrorResponse) {
            this.handleServerSideError(error);
        } else if (error.rejection instanceof HttpErrorResponse) {
            this.handleServerSideError(error.rejection);
        } else {
            this.handleClientSideError(error);
        }
    }

    private handleServerSideError(response: HttpErrorResponse): void {
        if (response.status === 0 || response.status === 502) {
            if (this.connectionErrorDialogOpen) return;
            this.connectionErrorDialogOpen = true;
            this.openConnectionErrorDialog().then(() => (this.connectionErrorDialogOpen = false));
            return;
        }
        let error = response.error || {};

        if (error.validationErrors) {
            this.openErrorDialog(error.validationErrors.map((e) => e.errorMessage).join('\n'), null, true);
            return;
        } else if (error.entityErrors) {
            this.handleEntityErrors(error.entityErrors);
            return;
        }

        if (_.isString(error) && error[0] === '{') {
            // breeze command with status 500
            try {
                error = JSON.parse(error);
            } catch {
                /* empty */
            }
        }

        this.openErrorDialog(error.errorMessage, error.exception, true);
    }

    private handleEntityErrors(entityErrors: any[]): void {
        this.openErrorDialog(
            entityErrors.map((e) => `${e.errorMessage} for property ${e.entityTypeName}`).join('\n'),
            null,
            true
        );
    }

    private handleClientSideError(error: Error) {
        console.error(error);
        this.remoteLogError(error);
        this.openErrorDialog(error.message, error.stack, false);
    }

    private openConnectionErrorDialog(): Promise<any> {
        return this.dialogService.open({
            template: { content: DialogConnectionErrorComponent },
            dialogSettings: { width: 420, minHeight: 0 }
        });
    }

    private openErrorDialog(errorMessage, stackTrace, serverSide): Promise<any> {
        return this.dialogService.open({
            template: {
                content: DialogErrorComponent,
                data: {
                    message: errorMessage,
                    isServerSideError: serverSide,
                    stackTrace
                }
            },
            dialogSettings: {
                width: 720,
                height: 520
            }
        });
    }

    remoteLogError(error) {
        if (error.toString().match('Cannot read property')) return;
        this.websocketService.error(error.toString()).catch((err) => console.error(err));
    }
}
